import { useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Upload, X, AlertCircle } from "lucide-react";

export default function ExcelImport({ onImported }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  async function downloadTemplate() {
    try {
      const token = localStorage.getItem("crm_token");
      const resp = await fetch(`${API}/condominios/import/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Falha ao gerar template");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_condominios.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Erro ao descarregar template");
    }
  }

  async function onFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setResult(null);
    try {
      const { data } = await api.post("/condominios/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(
        `Importação: ${data.imported} novos, ${data.updated} atualizados, ${data.skipped} ignorados`
      );
      onImported?.();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <>
      <button
        data-testid="excel-import-btn"
        onClick={() => setOpen(true)}
        className="bg-white border border-zinc-200 hover:border-neutral-950 text-neutral-950 rounded-sm px-4 py-2.5 font-manrope font-semibold text-sm flex items-center gap-2 transition-colors"
      >
        <FileSpreadsheet className="w-4 h-4" strokeWidth={1.8} />
        Importar Excel
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-sm w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="excel-import-modal"
          >
            <div className="border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
                  Importação
                </div>
                <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">
                  Importar condomínios
                </h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-8 space-y-5">
              <div className="text-sm font-manrope text-neutral-700 space-y-2">
                <p>Aceita ficheiros <strong>.xlsx</strong> ou <strong>.csv</strong>.</p>
                <p>
                  Colunas obrigatórias: <code className="text-xs">nome</code>,{" "}
                  <code className="text-xs">nif</code>, <code className="text-xs">morada</code>,{" "}
                  <code className="text-xs">administrador_nome</code>.
                </p>
                <p>
                  Opcionais: <code className="text-xs">codigo_postal</code>,{" "}
                  <code className="text-xs">localidade</code>,{" "}
                  <code className="text-xs">administrador_telefone</code>,{" "}
                  <code className="text-xs">administrador_email</code>,{" "}
                  <code className="text-xs">notas</code>.
                </p>
                <p className="text-neutral-500 text-xs">
                  Match por NIF: se já existir, é atualizado.
                </p>
              </div>

              <button
                data-testid="excel-download-template"
                onClick={downloadTemplate}
                className="w-full bg-white border border-zinc-200 hover:border-[#0047FF] hover:text-[#0047FF] text-neutral-900 rounded-sm px-4 py-2.5 font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <Download className="w-4 h-4" strokeWidth={1.8} />
                Descarregar template
              </button>

              <label
                data-testid="excel-file-label"
                className={`w-full border rounded-sm px-4 py-6 font-manrope text-sm cursor-pointer transition-colors flex flex-col items-center justify-center gap-2 text-center ${
                  uploading
                    ? "border-zinc-200 text-neutral-400 cursor-not-allowed"
                    : "bg-white border-dashed border-zinc-300 text-neutral-700 hover:border-[#0047FF] hover:text-[#0047FF]"
                }`}
              >
                <Upload className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-semibold">
                  {uploading ? "A carregar…" : "Escolher ficheiro"}
                </span>
                <span className="text-[11px] text-neutral-500">.xlsx ou .csv</span>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xlsm,.csv"
                  onChange={onFileChange}
                  disabled={uploading}
                  className="hidden"
                  data-testid="excel-file-input"
                />
              </label>

              {result && (
                <div className="border border-zinc-200 rounded-sm p-4 space-y-2" data-testid="excel-result">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <StatMini label="Novos" value={result.imported} accent="#16A34A" />
                    <StatMini label="Atualizados" value={result.updated} accent="#0047FF" />
                    <StatMini label="Ignorados" value={result.skipped} accent="#71717A" />
                  </div>
                  {result.errors?.length > 0 && (
                    <div className="pt-3 border-t border-zinc-100">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold mb-2 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} /> Erros ({result.errors.length})
                      </div>
                      <ul className="max-h-32 overflow-y-auto space-y-1 text-xs font-manrope text-neutral-600">
                        {result.errors.map((er, i) => (
                          <li key={`err-${er.linha ?? "x"}-${i}`}>Linha {er.linha}: {er.erro}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatMini({ label, value, accent }) {
  return (
    <div>
      <div className="text-2xl font-chivo font-black tracking-tight" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold mt-1">
        {label}
      </div>
    </div>
  );
}
